/**
 * ReminderRow — single reminder list item with swipe actions.
 * Swipe right → Reschedule. Swipe left → Delete.
 */

import React, { useRef, useMemo } from 'react';
import { View, Text, TouchableOpacity, Pressable, StyleSheet, Animated, Platform, type AccessibilityActionEvent, type AccessibilityActionInfo, type GestureResponderEvent } from 'react-native';
import { Swipeable, RectButton } from 'react-native-gesture-handler';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import * as Haptics from 'expo-haptics';
import type { Reminder } from '../../types/api';
import { getReminderStatus } from '../../types/api';
import { useTheme, ThemeColors } from '../../utils/theme';
import { getTagColor, getPrimaryTagColor } from '../../utils/tagColors';
import { formatOverdueAge, formatRecurrenceRule } from '../../utils/localization';
import { FONT_SIZE, FONT_FAMILY, SPACING, RADIUS, SHADOW } from '../../utils/design-tokens';

interface Props {
  reminder: Reminder;
  onPress: () => void;
  onDone?: () => void;
  onUndo?: () => void;
  onSnooze?: () => void;
  onReschedule?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onDelete?: () => void;
  relativeTime?: string;
  tagColors?: Record<string, string> | null;
  onTagPress?: (tag: string) => void;
  selectionMode?: boolean;
  selected?: boolean;
  onLongPress?: () => void;
  // V10: show the Google sync badge only when Google is actually connected
  // (a reminder can carry a stale google_event_id after a "keep events"
  // disconnect — we don't want to badge it as synced then).
  googleConnected?: boolean;
}

// Google brand blue — deliberately NOT red, so the sync badge never reads as
// "overdue" (overdue uses red). Keep in sync with the Calendar tab's Google color.
const GOOGLE_BLUE = '#4285F4';
const GOOGLE_RED = '#EA4335';

function ReminderRow({ reminder, onPress, onDone, onUndo, onSnooze, onReschedule, onPause, onResume, onDelete, relativeTime: relTime, tagColors, onTagPress, selectionMode = false, selected = false, onLongPress, googleConnected = false }: Props) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const { t, i18n } = useTranslation();
  const swipeableRef = useRef<Swipeable>(null);
  const longPressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressHandledRef = useRef(false);
  const suppressPressAfterLongPressRef = useRef(false);
  const suppressPressAfterSwipeRef = useRef(false);
  const pressStartPositionRef = useRef<{ pageX: number; pageY: number } | null>(null);
  const swipeInProgressRef = useRef(false);
  const status = getReminderStatus(reminder);
  const isRecurring = !!reminder.is_recurring;
  const isActive = status === 'active';
  const isPaused = status === 'paused';
  const isDone = status === 'done';
  const recurLabel = isRecurring
    ? formatRecurrenceRule(reminder.recur_json, i18n.language, reminder.until_utc) || t('reminder.recurring')
    : null;

  // Determine if overdue
  const isOverdue = isActive && reminder.run_at_utc && new Date(reminder.run_at_utc) < new Date();

  const exactTime = reminder.run_at_utc
    ? new Date(reminder.run_at_utc).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';
  const overdueAge = isOverdue ? formatOverdueAge(reminder.run_at_utc, i18n.language) : '';

  const parsedTags = useMemo(
    () => reminder.tags?.split(',').map((tag) => tag.trim()).filter(Boolean) ?? [],
    [reminder.tags]
  );
  const visibleTagLabels = parsedTags.slice(0, 2);
  const hiddenTagCount = Math.max(parsedTags.length - visibleTagLabels.length, 0);

  const closeSwipe = () => swipeableRef.current?.close();

  const accessibilityActions: AccessibilityActionInfo[] = selectionMode ? [] : [
    ...(isActive && onDone ? [{ name: 'markDone', label: t('reminders.swipeDone', 'Mark done') }] : []),
    ...(isActive && onReschedule ? [{ name: 'reschedule', label: t('reminder.rescheduleAction', 'Reschedule') }] : []),
    ...(isActive && isRecurring && onPause ? [{ name: 'pause', label: t('reminder.pause', 'Pause') }] : []),
    ...(isActive && !isRecurring && onSnooze ? [{ name: 'snooze', label: t('reminder.snooze', 'Snooze') }] : []),
    ...(isPaused && onResume ? [{ name: 'resume', label: t('reminder.resume', 'Resume') }] : []),
    ...(isDone && onUndo ? [{ name: 'undo', label: t('reminder.undo', 'Restore') }] : []),
    ...(onDelete ? [{ name: 'delete', label: t('reminders.swipeDelete', 'Delete') }] : []),
  ];

  const handleAccessibilityAction = (event: AccessibilityActionEvent) => {
    switch (event.nativeEvent.actionName) {
      case 'markDone': onDone?.(); break;
      case 'reschedule': onReschedule?.(); break;
      case 'pause': onPause?.(); break;
      case 'snooze': onSnooze?.(); break;
      case 'resume': onResume?.(); break;
      case 'undo': onUndo?.(); break;
      case 'delete': onDelete?.(); break;
    }
  };

  const renderRightActions = (_progress: Animated.AnimatedInterpolation<number>, dragX: Animated.AnimatedInterpolation<number>) => {
    const trans = dragX.interpolate({
      inputRange: [-80, 0],
      outputRange: [0, 80],
      extrapolate: 'clamp',
    });
    return (
      <Animated.View
        style={[s.swipeAction, s.swipeDelete, { transform: [{ translateX: trans }] }]}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        <RectButton
          style={s.swipeBtn}
          accessible={false}
          onPress={() => { closeSwipe(); onDelete?.(); }}
        >
          <Ionicons name="trash-outline" size={18} color="#fff" />
          <Text style={s.swipeLabel}>{t('reminders.swipeDelete')}</Text>
        </RectButton>
      </Animated.View>
    );
  };

  const renderLeftActions = (_progress: Animated.AnimatedInterpolation<number>, dragX: Animated.AnimatedInterpolation<number>) => {
    if (isDone && onUndo) {
      const trans = dragX.interpolate({
        inputRange: [0, 80],
        outputRange: [-80, 0],
        extrapolate: 'clamp',
      });
      return (
        <Animated.View
          style={[s.swipeLeftContainer, { transform: [{ translateX: trans }] }]}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
        >
          <RectButton
            style={[s.swipeBtn, s.swipeUndo]}
            accessible={false}
            onPress={() => { closeSwipe(); onUndo(); }}
          >
            <Ionicons name="arrow-undo" size={18} color="#fff" />
            <Text style={s.swipeLabel}>{t('reminder.undo')}</Text>
          </RectButton>
        </Animated.View>
      );
    }
    if (!isActive || (!onReschedule && !onPause && !onSnooze)) return null;
    const secondaryAction = isRecurring && onPause ? 'pause' : onSnooze ? 'snooze' : null;
    const actionWidth = (onReschedule ? 88 : 0) + (secondaryAction ? 76 : 0);
    const trans = dragX.interpolate({
      inputRange: [0, actionWidth],
      outputRange: [-actionWidth, 0],
      extrapolate: 'clamp',
    });
    return (
      <Animated.View
        style={[s.swipeLeftContainer, { transform: [{ translateX: trans }] }]}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        {onReschedule ? (
          <RectButton
            style={[s.swipeBtn, s.swipeReschedule, !secondaryAction && s.swipeOnly]}
            accessible={false}
            onPress={() => { closeSwipe(); onReschedule(); }}
          >
            <Ionicons name="calendar-outline" size={18} color="#fff" />
            <Text style={s.swipeLabel}>{t('reminder.rescheduleAction', 'Reschedule')}</Text>
          </RectButton>
        ) : null}
        {secondaryAction === 'pause' ? (
          <RectButton
            style={[s.swipeBtn, s.swipeSecondary]}
            accessible={false}
            onPress={() => { closeSwipe(); onPause?.(); }}
          >
            <Ionicons name="pause" size={17} color="#fff" />
            <Text style={s.swipeLabel}>{t('reminder.pause', 'Pause')}</Text>
          </RectButton>
        ) : secondaryAction === 'snooze' ? (
          <RectButton
            style={[s.swipeBtn, s.swipeSecondary]}
            accessible={false}
            onPress={() => { closeSwipe(); onSnooze?.(); }}
          >
            <Ionicons name="time-outline" size={18} color="#fff" />
            <Text style={s.swipeLabel}>{t('reminder.snooze', 'Snooze')}</Text>
          </RectButton>
        ) : null}
      </Animated.View>
    );
  };

  const playHapticBump = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
  };

  const clearLongPressTimeout = () => {
    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }
  };

  const beginSwipeLock = () => {
    swipeInProgressRef.current = true;
    suppressPressAfterSwipeRef.current = true;
    clearLongPressTimeout();
  };

  const releaseSwipeLock = () => {
    swipeInProgressRef.current = false;
    clearLongPressTimeout();
    pressStartPositionRef.current = null;
  };

  const handleRowPress = () => {
    if (suppressPressAfterSwipeRef.current) {
      suppressPressAfterSwipeRef.current = false;
      return;
    }
    if (suppressPressAfterLongPressRef.current) {
      suppressPressAfterLongPressRef.current = false;
      longPressHandledRef.current = false;
      return;
    }
    onPress();
  };
  const handleRowLongPress = () => {
    if (swipeInProgressRef.current) return;
    if (longPressHandledRef.current) return;
    longPressHandledRef.current = true;
    suppressPressAfterLongPressRef.current = true;
    onLongPress?.();
  };
  const handleRowPressIn = (event?: GestureResponderEvent) => {
    swipeInProgressRef.current = false;
    suppressPressAfterSwipeRef.current = false;
    pressStartPositionRef.current = event
      ? {
          pageX: event.nativeEvent.pageX,
          pageY: event.nativeEvent.pageY,
        }
      : null;
    if (Platform.OS !== 'web' || !onLongPress) return;
    clearLongPressTimeout();
    longPressTimeoutRef.current = setTimeout(handleRowLongPress, 360);
  };
  const handleRowPressMove = (event: GestureResponderEvent) => {
    if (Platform.OS !== 'web' || !onLongPress) return;
    const start = pressStartPositionRef.current;
    if (!start) return;
    const deltaX = event.nativeEvent.pageX - start.pageX;
    const deltaY = event.nativeEvent.pageY - start.pageY;
    if (Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
      beginSwipeLock();
      return;
    }
    if (Math.hypot(deltaX, deltaY) > 10) {
      clearLongPressTimeout();
    }
  };
  const handleRowPressOut = () => {
    releaseSwipeLock();
  };

  return (
    <Swipeable
      ref={swipeableRef}
      renderRightActions={selectionMode ? undefined : renderRightActions}
      renderLeftActions={selectionMode ? undefined : renderLeftActions}
      onSwipeableOpenStartDrag={selectionMode ? undefined : beginSwipeLock}
      onSwipeableCloseStartDrag={selectionMode ? undefined : beginSwipeLock}
      onSwipeableWillOpen={selectionMode ? undefined : playHapticBump}
      overshootLeft={false}
      overshootRight={false}
      friction={2}
    >
      <View style={[s.container, isOverdue && s.containerOverdue, selected && s.containerSelected]}>
      <Pressable
          style={s.rowContent}
          android_ripple={{ color: colors.primary + '12' }}
          onPress={handleRowPress}
          onLongPress={Platform.OS === 'web' ? undefined : handleRowLongPress}
          onPressIn={handleRowPressIn}
          onResponderMove={Platform.OS === 'web' ? handleRowPressMove : undefined}
          onPressOut={handleRowPressOut}
          delayLongPress={320}
          accessibilityRole={selectionMode ? 'checkbox' : 'button'}
          accessibilityState={selectionMode ? { checked: selected } : undefined}
          accessibilityHint={selectionMode ? t('reminders.toggleSelection', 'Toggles reminder selection') : t('reminder.swipeHint', 'Swipe for quick actions')}
          accessibilityActions={accessibilityActions}
          onAccessibilityAction={handleAccessibilityAction}
        >
        <View style={s.left}>
          {selectionMode ? (
            <View style={[s.selectionCircle, selected && s.selectionCircleSelected]}>
              {selected ? <Ionicons name="checkmark" size={12} color="#fff" /> : null}
            </View>
          ) : (
            <View style={[s.statusDot, isOverdue && s.statusDotOverdue, status === 'done' && s.statusDotDone, isPaused && s.statusDotPaused, !isOverdue && !isDone && !isPaused && reminder.tags ? { backgroundColor: getPrimaryTagColor(reminder.tags, tagColors) || undefined } : undefined]} />
          )}
          <View style={s.info}>
            <View style={s.titleRow}>
              <Text style={[s.title, status === 'done' && s.titleDone, isOverdue && s.titleOverdue]} numberOfLines={1}>
                {reminder.body}
              </Text>
              <View style={s.titleBadges}>
                {/* Google badge: RED G = imported from Google; BLUE G =
                    synced to Google (only shown while connected). */}
                {reminder.source === 'google' ? (
                  <View
                    style={[s.recurringPill, { backgroundColor: GOOGLE_RED + '14' }]}
                    accessible
                    accessibilityLabel={t('reminder.fromGoogle', 'From Google Calendar')}
                  >
                    <Ionicons name="logo-google" size={10} color={GOOGLE_RED} />
                  </View>
                ) : googleConnected && reminder.google_event_id ? (
                  <View
                    style={[s.recurringPill, { backgroundColor: GOOGLE_BLUE + '14' }]}
                    accessible
                    accessibilityLabel={t('reminder.syncedToGoogle', 'Synced to Google Calendar')}
                  >
                    <Ionicons name="logo-google" size={10} color={GOOGLE_BLUE} />
                  </View>
                ) : null}
                {isRecurring && recurLabel ? (
                  <View style={s.recurringPill} accessible accessibilityLabel={recurLabel}>
                    <Ionicons name="repeat" size={10} color={colors.primary} />
                  </View>
                ) : null}
                {visibleTagLabels.slice(0, 1).map((tag) => {
                  const tagColor = getTagColor(tag, tagColors);
                  return (
                    <Pressable
                      key={tag}
                      onPress={() => onTagPress?.(tag)}
                      disabled={selectionMode}
                      hitSlop={4}
                      accessibilityRole="button"
                      accessibilityLabel={`Tag ${tag}`}
                      style={[s.tagPill, { backgroundColor: tagColor + '10' }]}
                    >
                      <View style={[s.tagPillDot, { backgroundColor: tagColor }]} />
                    </Pressable>
                  );
                })}
                {hiddenTagCount > 0 ? <Text style={s.moreTags}>+{hiddenTagCount}</Text> : null}
              </View>
            </View>
            <View style={s.meta}>
              {isOverdue && exactTime ? (
                <Text style={s.dateLine} numberOfLines={1}>
                  <Text style={s.exactTime}>{exactTime}</Text>
                  {overdueAge ? <Text style={s.timeOverdue}> · {overdueAge}</Text> : null}
                </Text>
              ) : (
                <>
                  {relTime ? <Text style={s.time} numberOfLines={1}>{relTime}</Text> : null}
                  {exactTime ? <Text style={s.exactTime} numberOfLines={1}>{exactTime}</Text> : null}
                </>
              )}
              {isPaused && <Text style={s.pausedLabel}>{t('reminder.paused')}</Text>}
            </View>
          </View>
        </View>
      </Pressable>

        {!selectionMode && <View style={s.actionsOverlay}>
          {isActive && onDone && (
            <TouchableOpacity
              style={s.doneBtn}
              onPress={() => { Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); onDone(); }}
              activeOpacity={0.7}
              hitSlop={7}
              accessibilityRole="button"
              accessibilityLabel={t('reminders.swipeDone', 'Mark done')}
              accessibilityHint={t('reminder.markDoneHint', 'Marks this reminder as completed')}
            >
              <Ionicons name="checkmark" size={18} color="#fff" />
            </TouchableOpacity>
          )}
          {isPaused && onResume && (
            <TouchableOpacity
              style={s.resumeBtn}
              onPress={() => { Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); onResume(); }}
              activeOpacity={0.7}
              hitSlop={7}
              accessibilityRole="button"
              accessibilityLabel={t('reminder.resume', 'Resume')}
              accessibilityHint={t('reminder.resumeHint', 'Resumes paused reminder notifications')}
            >
              <Ionicons name="play" size={16} color="#fff" />
            </TouchableOpacity>
          )}
          {isDone && onUndo && (
            <TouchableOpacity
              style={s.undoBtn}
              onPress={() => { Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); onUndo(); }}
              activeOpacity={0.7}
              hitSlop={7}
              accessibilityRole="button"
              accessibilityLabel={t('reminder.undoDone', 'Undo')}
              accessibilityHint={t('reminder.undoHint', 'Marks this reminder as not done')}
            >
              <Ionicons name="arrow-undo" size={16} color="#fff" />
            </TouchableOpacity>
          )}
        </View>}
      </View>
    </Swipeable>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.card,
    borderRadius: RADIUS.xl,
    marginHorizontal: SPACING.base,
    marginVertical: SPACING.xs,
    borderWidth: 0.5,
    borderColor: colors.border,
    ...SHADOW.card,
  },
  rowContent: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: SPACING.base,
    paddingTop: 10,
    paddingBottom: 10,
    paddingRight: SPACING.sm,
  },
  containerOverdue: {
    borderColor: colors.error + '38',
    borderLeftColor: colors.error + '90',
    borderLeftWidth: 3,
    backgroundColor: colors.card,
  },
  containerSelected: {
    borderColor: colors.primary + '70',
    backgroundColor: colors.primary + '0F',
  },
  left: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.success,
    marginTop: 4,
  },
  statusDotOverdue: {
    backgroundColor: colors.error,
  },
  statusDotDone: {
    backgroundColor: colors.textSecondary,
  },
  statusDotPaused: {
    backgroundColor: colors.warning,
  },
  selectionCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.surface,
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectionCircleSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.primary,
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  titleRow: {
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  titleBadges: {
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  title: {
    flexShrink: 1,
    fontSize: FONT_SIZE.base,
    fontFamily: FONT_FAMILY.bodyMedium,
    color: colors.text,
    lineHeight: 20,
  },
  titleDone: {
    textDecorationLine: 'line-through',
    color: colors.textDim,
  },
  titleOverdue: {
    color: colors.text,
  },
  meta: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 6,
    marginTop: 2,
  },
  time: {
    fontSize: FONT_SIZE.xs,
    color: colors.textSecondary,
  },
  timeOverdue: {
    color: colors.error,
    fontWeight: '600',
  },
  dateLine: {
    flexShrink: 1,
    fontSize: FONT_SIZE.xs,
  },
  exactTime: {
    fontSize: FONT_SIZE.xs,
    color: colors.textSecondary,
  },
  recurringPill: {
    height: 16,
    width: 16,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: RADIUS.full,
    backgroundColor: colors.primary + '10',
  },
  recurring: {
    flexShrink: 1,
    fontSize: 10,
    lineHeight: 12,
    color: colors.primary,
    fontFamily: FONT_FAMILY.bodySemibold,
  },
  pausedLabel: {
    fontSize: FONT_SIZE.xs,
    color: colors.warning,
    fontWeight: '600',
  },
  tagPill: {
    height: 16,
    width: 16,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: RADIUS.full,
  },
  tagPillDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  tagText: {
    flexShrink: 1,
    fontSize: 10,
    lineHeight: 12,
    color: colors.primary,
    fontFamily: FONT_FAMILY.bodySemibold,
  },
  moreTags: {
    fontSize: 10,
    lineHeight: 12,
    color: colors.textDim,
    fontFamily: FONT_FAMILY.bodySemibold,
  },
  actions: {
    flexDirection: 'row',
    gap: 6,
  },
  actionsOverlay: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingRight: SPACING.md,
    flexShrink: 0,
  },
  doneBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.success,
    justifyContent: 'center',
    alignItems: 'center',
  },
  resumeBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  undoBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  swipeAction: {
    justifyContent: 'center',
    marginVertical: 4,
  },
  swipeDelete: {
    backgroundColor: colors.error,
    borderRadius: 20,
    marginRight: 16,
  },
  swipeLeftContainer: {
    flexDirection: 'row',
    marginVertical: 4,
    marginLeft: 16,
  },
  swipeBtn: {
    justifyContent: 'center',
    alignItems: 'center',
    width: 70,
    paddingVertical: 8,
  },
  swipeDone: {
    backgroundColor: colors.success,
    borderTopLeftRadius: 20,
    borderBottomLeftRadius: 20,
  },
  swipeSnooze: {
    backgroundColor: colors.warning,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
  },
  swipeUndo: {
    backgroundColor: colors.primary,
    borderRadius: 20,
  },
  swipeReschedule: {
    backgroundColor: colors.primary,
    width: 88,
    borderTopLeftRadius: 20,
    borderBottomLeftRadius: 20,
  },
  swipeSecondary: {
    width: 76,
    backgroundColor: colors.warning,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
  },
  swipeOnly: {
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
  },
  swipeLabel: {
    color: '#fff',
    fontSize: FONT_SIZE.xs,
    fontFamily: FONT_FAMILY.bodySemibold,
    marginTop: 2,
  },
});

export default React.memo(ReminderRow, (prev, next) => {
  return (
    prev.reminder.user_seq === next.reminder.user_seq &&
    prev.reminder.is_done === next.reminder.is_done &&
    prev.reminder.is_paused === next.reminder.is_paused &&
    prev.reminder.is_recurring === next.reminder.is_recurring &&
    prev.reminder.recur_json === next.reminder.recur_json &&
    prev.reminder.run_at_utc === next.reminder.run_at_utc &&
    prev.reminder.next_run_utc === next.reminder.next_run_utc &&
    prev.reminder.body === next.reminder.body &&
    prev.relativeTime === next.relativeTime &&
    !!prev.onPause === !!next.onPause &&
    !!prev.onSnooze === !!next.onSnooze &&
    prev.tagColors === next.tagColors &&
    prev.selectionMode === next.selectionMode &&
    prev.selected === next.selected
  );
});
